import { useTranslation } from 'react-i18next';
import { Box, Chip, styled, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { VoiceTranscript } from './types';

export const WidgetResponseTranscript = ({
  transcript,
  widgetByBatchId,
}: {
  transcript: VoiceTranscript;
  widgetByBatchId: Map<string, VoiceTranscript>;
}) => {
  const widget = transcript.batch_id
    ? widgetByBatchId.get(transcript.batch_id)
    : undefined;

  if (!widget) {
    return null;
  }

  if (widget.widget_type === 'form') {
    return (
      <FormResponse
        formFields={
          widget.widget_data?.form_fields as
            | Array<{ output_variable: string; placeholder: string }>
            | undefined
        }
        response={transcript}
      />
    );
  }

  if (widget.widget_type === 'buttons') {
    return (
      <ButtonResponse
        options={
          widget.widget_data?.options as
            | Array<{ display_name: string; value: string }>
            | undefined
        }
        outputVariable={
          widget.widget_data?.output_variable as string | undefined
        }
        response={transcript}
      />
    );
  }

  if (widget.widget_type === 'dynamic_card') {
    return <DynamicCardResponse response={transcript} widget={widget} />;
  }

  return null;
};

const ButtonResponse = ({
  options,
  outputVariable,
  response,
}: {
  options: Array<{ display_name: string; value: string }> | undefined;
  outputVariable: string | undefined;
  response: VoiceTranscript;
}) => {
  const { palette } = useTheme();
  const selectedValue = outputVariable
    ? (response.widget_response?.[outputVariable] as string | undefined)
    : (Object.values(response.widget_response ?? {})[0] as string | undefined);

  if (!options || options.length === 0) {
    if (selectedValue) {
      return (
        <ChipsContainer>
          <Chip
            label={selectedValue}
            sx={{
              backgroundColor: palette.colors.blue[900],
              color: palette.colors.white,
              fontWeight: 500,
            }}
          />
        </ChipsContainer>
      );
    }
    return null;
  }

  return (
    <ChipsContainer>
      {options.map(option => {
        const isSelected = option.value === selectedValue;
        return (
          <Chip
            key={option.value}
            label={option.display_name}
            sx={{
              backgroundColor: isSelected
                ? palette.colors.blue[900]
                : 'transparent',
              border: isSelected
                ? 'none'
                : `1px solid ${palette.colors.blue[900]}`,
              color: isSelected
                ? palette.colors.white
                : palette.colors.blue[900],
              fontWeight: 500,
            }}
            variant={isSelected ? 'filled' : 'outlined'}
          />
        );
      })}
    </ChipsContainer>
  );
};

const FormResponse = ({
  formFields,
  response,
}: {
  formFields:
    | Array<{ output_variable: string; placeholder: string }>
    | undefined;
  response: VoiceTranscript;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const formData = response.widget_response as
    | Record<string, string>
    | undefined;
  if (!formData || Object.keys(formData).length === 0) {
    return null;
  }

  const getDisplayName = (cvId: string): string => {
    const field = formFields?.find(f => f.output_variable === cvId);
    return field?.placeholder || cvId;
  };

  return (
    <FormCard sx={{ backgroundColor: palette.colors.blue[900] }}>
      {Object.entries(formData).map(([key, value]) => (
        <Typography color={palette.colors.white} key={key} variant='font14'>
          {t('common.nameValuePair', {
            name: getDisplayName(key),
            value: String(value),
          })}
        </Typography>
      ))}
    </FormCard>
  );
};

const DynamicCardResponse = ({
  response,
  widget,
}: {
  response: VoiceTranscript;
  widget: VoiceTranscript;
}) => {
  const { palette } = useTheme();
  const cards = widget.widget_data?.cards as
    | Array<{
        displayable_dynamic_sections: Array<{
          components: Array<{ label?: string; value: string }>;
          type: string;
        }>;
        value: string;
      }>
    | undefined;
  const outputVariable = widget.widget_data?.output_variable as
    | string
    | undefined;
  const fallbackOutputVariable = widget.widget_data
    ?.fallback_output_variable as string | undefined;
  const fallbackText =
    (widget.widget_data?.fallback_text as string) || 'None of these';

  const selectedValue = outputVariable
    ? (response.widget_response?.[outputVariable] as string | undefined)
    : undefined;
  const isFallbackSelected = fallbackOutputVariable
    ? Boolean(response.widget_response?.[fallbackOutputVariable])
    : false;

  const getCardTitle = (card: NonNullable<typeof cards>[number]): string => {
    for (const section of card.displayable_dynamic_sections ?? []) {
      if (section.type === 'title' && section.components[0]) {
        const comp = section.components[0];
        return [comp.label, comp.value].filter(Boolean).join(' ');
      }
    }
    return card.value;
  };

  if (!cards) {
    return null;
  }

  return (
    <ChipsContainer>
      {cards.map(card => {
        const isSelected = card.value === selectedValue;
        return (
          <Chip
            key={card.value}
            label={getCardTitle(card)}
            sx={{
              backgroundColor: isSelected
                ? palette.colors.blue[900]
                : 'transparent',
              border: isSelected
                ? 'none'
                : `1px solid ${palette.colors.blue[900]}`,
              color: isSelected
                ? palette.colors.white
                : palette.colors.blue[900],
              fontWeight: 500,
            }}
            variant={isSelected ? 'filled' : 'outlined'}
          />
        );
      })}
      {isFallbackSelected && (
        <Chip
          label={fallbackText}
          sx={{
            backgroundColor: palette.colors.blue[900],
            color: palette.colors.white,
            fontWeight: 500,
          }}
        />
      )}
    </ChipsContainer>
  );
};

const ChipsContainer = styled(Box)`
  align-self: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 400px;
`;

const FormCard = styled(Box)`
  align-self: flex-end;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
`;
